import { useEffect, useState } from "react";
import { useLocalStorageState } from "../../../../../hooks/useLocalStorageState";
import {
  INTEGRATION_SYNC_ALL_KEY,
  INTEGRATION_SYNC_NEW_KEY,
  REFETCH_INTERVAL_MS,
  UNKNOWN_AUTHORIZATION_DATE,
} from "../config";
import { useConstantContactAuthDataQuery } from "../../../../../api/constantContact/useConstantContactAuthDataQuery";
import { uiNotification } from "../../../../../services/UINotificationService";
import { useSaveConstantContactAuthDataMutation } from "../../../../../api/constantContact/useSaveConstantContactAuthDataMutation";

const parseBoolean = (value) => {
  return value === "true";
};

export function useIntegration() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [url, setUrl] = useState("");
  const [date, setDate] = useState(null);
  const [enableRefetchInterval, setEnableRefetchInterval] = useState(false);
  const [windowRef, setWindowRef] = useState(null);

  const { mutateAsync: saveIntegrationData } =
    useSaveConstantContactAuthDataMutation({
      onError: (error) => {
        const message =
          error?.response?.data?.message ||
          "Unable to save integration data. Try again later.";
        uiNotification.error(message);
      },
    });

  const [syncAll, setSyncAll] = useLocalStorageState(
    INTEGRATION_SYNC_ALL_KEY,
    false,
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const [syncNew, setSyncNew] = useLocalStorageState(
    INTEGRATION_SYNC_NEW_KEY,
    false,
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const { isLoading } = useConstantContactAuthDataQuery({
    refetchInterval: enableRefetchInterval ? REFETCH_INTERVAL_MS : false,
    refetchIntervalInBackground: enableRefetchInterval,
    onError: () => {
      uiNotification.error(
        "Unable to fetch integration data. Try again later.",
      );
    },
    onSuccess: (data) => {
      const enabled = data.isAuth;
      if (!enabled && !enableRefetchInterval) {
        setIsEnabled(false);
      }
      if (enabled) {
        setIsEnabled(true);
        setEnableRefetchInterval(false);
        // Close the popup if it is still open (e.g. polling detected auth success
        // before the message-handler path had a chance to close it).
        // We only reach this branch when the backend already has the code saved,
        // so closing here is safe.
        setWindowRef((prev) => {
          if (prev && !prev.closed) {
            prev.close();
          }
          return null;
        });
      }
      setUrl(data.url);
      setDate(enabled ? data.date || UNKNOWN_AUTHORIZATION_DATE : null);
      setSyncAll(data.syncAll);
      setSyncNew(data.syncNew);
    },
  });

  const onAuthorize = () => {
    const newWindow = window.open(
      url,
      "Ratting",
      "width=550,height=550,left=150,top=200,toolbar=0,status=0,scrollbars=1",
    );
    if (newWindow) {
      setWindowRef(newWindow);
      setEnableRefetchInterval(true);
    }
  };

  useEffect(() => {
    if (!windowRef) {
      return;
    }

    const handleMessage = (event) => {
      // eslint-disable-next-line no-console
      console.log("Received message event:", event);

      // Check source FIRST — this is the most reliable guard because it is a direct
      // reference comparison to the exact popup window we opened.  Messages from any
      // other window (browser extensions, iframes, devtools, etc.) are silently ignored.
      if (event.source !== windowRef) {
        // eslint-disable-next-line no-console
        console.warn(
          "Source mismatch — message is NOT from our popup, ignoring:",
          event.source,
          windowRef,
        );
        return;
      }

      // Origin check as a secondary security layer.
      // In most browsers this will always match because the popup navigated to our
      // own callback URL before posting the message.  However, some Safari versions
      // (or other edge-cases) can produce an unexpected origin even though the source
      // IS our popup window.  In that situation we log a warning but still process
      // the message — the source check above is already a strong enough guard.
      if (event.origin !== window.location.origin) {
        // eslint-disable-next-line no-console
        console.warn(
          "Origin mismatch from our popup window (may be a browser quirk):",
          event.origin,
          "expected:",
          window.location.origin,
          "— continuing because source check passed",
        );
      }

      const message = event.data;
      // eslint-disable-next-line no-console
      console.log("Received message:", message);

      if (
        message &&
        message.childWindow &&
        message.childWindow === "response"
      ) {
        if (message.query) {
          const { code, state } = message.query;
          if (code) {
            // eslint-disable-next-line no-console
            console.log("Code received, saving integration data...");
            saveIntegrationData({
              code,
              state,
              isEnabled: true,
              syncAll,
              syncNew,
            }).then(() => {
              // Close popup ONLY after the code has been saved successfully
              // eslint-disable-next-line no-console
              console.log("Integration data saved, closing popup");
              if (windowRef && !windowRef.closed) {
                windowRef.close();
              }
            });
          } else {
            // eslint-disable-next-line no-console
            console.warn(
              "Message received from popup but no code present — popup will remain open",
              message.query,
            );
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    const interval = setInterval(() => {
      if (windowRef.closed) {
        // eslint-disable-next-line no-console
        console.log("Window closed");
        clearInterval(interval);
        window.removeEventListener("message", handleMessage);
        setTimeout(() => {
          setEnableRefetchInterval(false);
          setWindowRef(null);
        }, REFETCH_INTERVAL_MS);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("message", handleMessage);
    };
  }, [windowRef, syncAll, syncNew]);

  const handleIsEnabled = (next) => {
    const next_ = typeof next === "function" ? next(isEnabled) : next;
    if (next_) {
      setIsEnabled(true);
    } else {
      setIsEnabled(false);
      setEnableRefetchInterval(false);
      saveIntegrationData({
        code: null,
        state: null,
        isEnabled: false,
        syncAll: false,
        syncNew: false,
      }).catch(() => {
        setIsEnabled(true);
      });
    }
  };

  useEffect(() => {
    if (isEnabled && !isLoading && !enableRefetchInterval) {
      saveIntegrationData({
        code: null,
        state: null,
        isEnabled: true,
        syncAll,
        syncNew,
      });
    }
  }, [syncAll, syncNew]);

  useEffect(() => {
    if (!isEnabled) {
      setEnableRefetchInterval(false);
    }
  }, [isEnabled]);

  return {
    isEnabled,
    setIsEnabled: handleIsEnabled,
    syncAll,
    setSyncAll,
    syncNew,
    setSyncNew,
    isLoading,
    date,
    onAuthorize,
  };
}
